"use client"

import type React from "react"

import { useEffect, useState, useMemo, useRef } from "react"
import { Link } from "@/i18n/navigation";
import { Button } from "@/components/ui/button"
import { UserMenu } from "./UserMenu"
import { useAuth } from "@/contexts/AuthContext"
import {
  Search,
  Building2,
  HelpCircle,
  LayoutDashboard,
  Phone,
  Mail,
  Menu,
  ShoppingBag,
  BookText,
  Landmark,
  Calendar,
  MapPin,
} from "lucide-react"
import { useCartStore } from "@/stores/cartStore"
import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"
import { useUserRoles } from "@/hooks/useUserRoles"
import { Logo } from "@/components/ui/logo"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "@/components/ui/sheet"
import { LanguageSwitcher } from "@/components/language-switcher"
import { useTranslations } from "next-intl"
import {
} from "@/components/ui/dropdown-menu"
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu"

interface NavLink {
  href: string
  text: string
  icon?: React.ElementType
  children?: {
    title: string
    href: string
    description?: string
    icon?: React.ElementType
  }[]
}

// Navigation Links Component
function NavigationLinks({ links, className }: { links: NavLink[]; className?: string }) {
  const t = useTranslations("navigation.header")

  return (
    <NavigationMenu className={className}>
      <NavigationMenuList>
        {links.map((link) => {
          // Simple link without dropdown
          if (!link.children) {
            return (
              <NavigationMenuItem key={link.href}>
                <Link href={link.href} className={cn(
                      navigationMenuTriggerStyle(),
                      "bg-white hover:bg-amber-50 hover:text-amber-800",
                      link.text === "Blog" && "text-amber-700 hover:text-amber-800",
                )}>
                    <span className="flex items-center gap-1.5">
                      {link.icon && <link.icon className={cn("h-4 w-4", link.text === "Blog" && "text-amber-600")} />}
                      {link.text}
                    </span>
                </Link>
              </NavigationMenuItem>
            )
          }

          // Link with dropdown
          return (
            <NavigationMenuItem key={link.href}>
              <NavigationMenuTrigger
                className={cn(
                  "bg-white hover:bg-amber-50 hover:text-amber-800",
                  link.text === "Tours" && "text-amber-700 hover:text-amber-800",
                )}
              >
                <span className="flex items-center gap-1.5">
                  {link.icon && <link.icon className={cn("h-4 w-4", link.text === "Tours" && "text-amber-600")} />}
                  {link.text}
                </span>
              </NavigationMenuTrigger>
              <NavigationMenuContent>
                <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2 lg:w-[600px]">
                  {link.children.map((child) => (
                    <li key={child.href} className="row-span-1">
                      <NavigationMenuLink asChild>
                        <Link
                          href={child.href}
                          className="flex h-full w-full select-none flex-col justify-between rounded-md bg-white p-4 no-underline outline-none hover:bg-amber-50 hover:text-amber-800 focus:shadow-md"
                        >
                          <div className="flex items-center gap-2 mb-2">
                            {child.icon && <child.icon className="h-5 w-5 text-amber-600" />}
                            <span className="text-sm font-medium">{child.title}</span>
                          </div>
                          {child.description && (
                            <span className="line-clamp-2 text-sm text-muted-foreground">{child.description}</span>
                          )}
                        </Link>
                      </NavigationMenuLink>
                    </li>
                  ))}
                </ul>
              </NavigationMenuContent>
            </NavigationMenuItem>
          )
        })}
      </NavigationMenuList>
    </NavigationMenu>
  )
}

// Company Button Component
function CompanyButton({ isCompany, t }: { isCompany: boolean; t: any }) {
  if (!isCompany) return null

  return (
    <Button
      variant="outline"
      asChild
      className="border-amber-600 text-amber-700 hover:bg-amber-50 hover:text-amber-800 hover:border-amber-700"
    >
      <Link href="/company/dashboard" className="flex items-center gap-1.5">
        <Building2 className="h-4 w-4" />
        {t("agencyPanel")}
      </Link>
    </Button>
  )
}

// Mobile Menu Links Component
function MobileMenuLinks({ links, t }: { links: NavLink[]; t: any }) {
  return (
    <div className="space-y-1">
      {links.map((link) => (
        <div key={link.href}>
          {!link.children ? (
            <SheetClose asChild>
              <Link
                href={link.href}
                className={cn(
                  "flex items-center gap-2 py-3 px-2 text-base font-medium text-foreground hover:text-amber-700 hover:bg-amber-50 rounded-md transition-colors",
                  link.text === "Blog" && "text-amber-700 hover:text-amber-800",
                )}
              >
                {link.icon && <link.icon className={cn("h-5 w-5", link.text === "Blog" && "text-amber-600")} />}
                {link.text}
              </Link>
            </SheetClose>
          ) : (
            <div className="space-y-1">
              <div
                className={cn(
                  "flex items-center gap-2 py-3 px-2 text-base font-medium text-foreground",
                  link.text === "Tours" && "text-amber-700",
                )}
              >
                {link.icon && <link.icon className={cn("h-5 w-5", link.text === "Tours" && "text-amber-600")} />}
                {link.text}
              </div>
              <div className="pl-4 border-l-2 border-amber-100 ml-2 space-y-1">
                {link.children.map((child) => (
                  <SheetClose key={child.href} asChild>
                    <Link
                      href={child.href}
                      className="flex items-center gap-2 py-2 px-2 text-sm font-medium text-muted-foreground hover:text-amber-700 hover:bg-amber-50 rounded-md transition-colors"
                    >
                      {child.icon && <child.icon className="h-4 w-4 text-amber-600" />}
                      {child.title}
                    </Link>
                  </SheetClose>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

export default function Header() {
  const { user, isLoading: isAuthLoading } = useAuth()
  const { items, fetchCart, clearCart } = useCartStore()
  const [isScrolled, setIsScrolled] = useState(false)
  const t = useTranslations("navigation.header")
  const headerRef = useRef<HTMLElement>(null)

  const {
    roles,
    isLoading: rolesLoading,
    error: rolesError,
  } = useUserRoles(user?.id)

  useEffect(() => {
    const setHeaderHeight = () => {
      if (headerRef.current) {
        document.documentElement.style.setProperty("--header-height", `${headerRef.current.offsetHeight}px`)
      }
    }
    setHeaderHeight()
    window.addEventListener("resize", setHeaderHeight)
    return () => window.removeEventListener("resize", setHeaderHeight)
  }, [])

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10)
    }
    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => {
      window.removeEventListener("scroll", handleScroll)
    }
  }, [])

  useEffect(() => {
    let isMounted = true
    if (!isAuthLoading) {
      if (user && isMounted) {
        fetchCart().catch(cartError => {
          console.error("Error fetching cart in Header:", cartError)
        })
      } else if (!user && isMounted) {
        clearCart()
      }
    }

    return () => {
      isMounted = false
    }
  }, [user, isAuthLoading, fetchCart, clearCart])

  const headerLinks = useMemo(() => {
    const toursDropdown = {
      href: "/tours",
      text: t("tours"),
      icon: Landmark,
      children: [
        { title: t("allTours"), href: "/tours", description: t("allToursDesc"), icon: Landmark },
        { title: t("hajjTours"), href: "/tours?type=HAC", description: t("hajjToursDesc"), icon: Calendar },
        { title: t("umrahTours"), href: "/tours?type=UMRE", description: t("umrahToursDesc"), icon: MapPin },
        { title: t("compareTours"), href: "/compare", description: t("compareToursDesc"), icon: Search },
      ],
    }
    const publicLinks = [toursDropdown, { href: "/blog", text: t("blog"), icon: BookText }]

    if (isAuthLoading || (user && rolesLoading) || !user) {
      return publicLinks
    }
    if (rolesError) {
      console.error("Error fetching roles for header links, showing public links.", rolesError)
      return publicLinks
    }

    const safeRoles = Array.isArray(roles) ? roles : []
    if (safeRoles.includes("admin")) {
      return [
        { href: "/admin/dashboard", text: "Panel", icon: LayoutDashboard },
        { href: "/blog", text: t("blog"), icon: BookText },
      ]
    }
    return publicLinks
  }, [isAuthLoading, user, roles, rolesLoading, rolesError, t])

  const isCompany = useMemo(() => {
    if (!user || rolesLoading || rolesError || !Array.isArray(roles)) return false
    return roles.includes("company")
  }, [user, roles, rolesLoading, rolesError])

  const shouldShowCart = useMemo(() => {
    if (!user || rolesLoading || rolesError || !Array.isArray(roles)) return false
    return !roles.includes("admin")
  }, [user, roles, rolesLoading, rolesError])

  const isLoading = isAuthLoading || (!!user && rolesLoading)

  return (
    <header
      ref={headerRef}
      className={cn(
        "sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 transition-shadow duration-200",
        isScrolled && "shadow-md border-transparent",
      )}
    >
      <div className="bg-amber-50 border-b border-amber-100">
        <div className="container mx-auto px-4">
          <div className="hidden md:flex items-center justify-between py-2 text-sm">
            <div className="flex items-center gap-4 text-amber-800">
              <span className="flex items-center gap-1.5">
                <Phone className="h-3.5 w-3.5" /> {t("contactPhone")}
              </span>
              <span className="flex items-center gap-1.5">
                <Mail className="h-3.5 w-3.5" /> {t("contactEmail")}
              </span>
            </div>
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                asChild
                className="text-amber-700 hover:text-amber-900 hover:bg-amber-100"
              >
                <Link href="/compare" className="flex items-center gap-1.5">
                  {t("compareTours")}
                </Link>
              </Button>
              <div className="w-px h-4 bg-amber-200" />
              <Button
                variant="ghost"
                size="sm"
                asChild
                className="text-amber-700 hover:text-amber-900 hover:bg-amber-100"
              >
                <Link href="/help" className="flex items-center gap-1.5">
                  <HelpCircle className="h-4 w-4" />
                  {t("help")}
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center gap-6">
              <Link href="/" className="flex-shrink-0">
                <Logo />
              </Link>
              <nav className="hidden lg:block">
                {isLoading ? <Skeleton className="h-8 w-[200px]" /> : <NavigationLinks links={headerLinks} />}
              </nav>
            </div>

            <div className="flex items-center gap-2 md:gap-4">
              {isLoading ? (
                <div className="flex items-center gap-2 md:gap-4">
                  <Skeleton className="h-9 w-9 rounded-full" />
                  <Skeleton className="h-9 w-[60px] rounded-md" />
                  <Skeleton className="h-9 w-[80px] rounded-md" />
                </div>
              ) : (
                <>
                  <LanguageSwitcher />
                  {shouldShowCart && (
                    <Button variant="ghost" size="icon" asChild className="relative">
                      <Link href="/cart">
                        <ShoppingBag className="h-5 w-5" />
                        {items.length > 0 && (
                          <Badge
                            variant="secondary"
                            className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-amber-100 text-amber-800 hover:bg-amber-100"
                          >
                            {items.length}
                          </Badge>
                        )}
                        <span className="sr-only">{t("cart")}</span>
                      </Link>
                    </Button>
                  )}
                  <div className="hidden md:block">
                    {user && <CompanyButton isCompany={isCompany} t={t} />}
                  </div>
                  {!user ? (
                    <div className="hidden md:flex items-center gap-2">
                      <Button variant="ghost" asChild className="text-gray-700 hover:text-amber-700 hover:bg-amber-50">
                        <Link href="/login">{t("login")}</Link>
                      </Button>
                      <Button
                        variant="default"
                        asChild
                        className="bg-amber-600 hover:bg-amber-700 text-white shadow-sm"
                      >
                        <Link href="/register">{t("register")}</Link>
                      </Button>
                    </div>
                  ) : (
                    <UserMenu />
                  )}
                  <Sheet>
                    <SheetTrigger asChild>
                      <Button variant="ghost" size="icon" className="lg:hidden">
                        <Menu className="h-5 w-5" />
                        <span className="sr-only">{t("menu")}</span>
                      </Button>
                    </SheetTrigger>
                    <SheetContent side="right" className="w-[300px] sm:w-[350px]">
                      <SheetHeader className="text-left">
                        <SheetTitle className="flex items-center gap-2">
                          <Logo />
                        </SheetTitle>
                        <SheetDescription>{t("mobileDescription")}</SheetDescription>
                      </SheetHeader>
                      <div className="mt-6 flex flex-col h-[calc(100vh-180px)]">
                        <div className="flex-grow overflow-y-auto">
                          {isLoading ? <Skeleton className="h-20 w-full" /> : <MobileMenuLinks links={headerLinks} t={t} />}
                          {user && isCompany && (
                            <div className="mt-4 pt-4 border-t border-amber-100">
                              <SheetClose asChild>
                                <Link
                                  href="/company/dashboard"
                                  className="flex items-center gap-2 py-3 px-2 text-base font-medium text-amber-700 hover:text-amber-800 hover:bg-amber-50 rounded-md transition-colors"
                                >
                                  <Building2 className="h-5 w-5" />
                                  {t("agencyPanel")}
                                </Link>
                              </SheetClose>
                            </div>
                          )}
                        </div>
                        <div className="mt-4 pt-4 border-t border-amber-100">
                          <SheetClose asChild>
                            <Link
                              href="/help"
                              className="flex items-center gap-2 py-3 px-2 text-base font-medium text-foreground hover:text-amber-700 hover:bg-amber-50 rounded-md transition-colors"
                            >
                              <HelpCircle className="h-5 w-5" />
                              {t("help")}
                            </Link>
                          </SheetClose>
                        </div>
                        {!user ? (
                          <div className="mt-auto pt-4 border-t border-amber-100">
                            <div className="grid grid-cols-2 gap-2">
                              <SheetClose asChild>
                                <Button variant="outline" asChild className="w-full">
                                  <Link href="/login">{t("login")}</Link>
                                </Button>
                              </SheetClose>
                              <SheetClose asChild>
                                <Button
                                  variant="default"
                                  asChild
                                  className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                                >
                                  <Link href="/register">{t("register")}</Link>
                                </Button>
                              </SheetClose>
                            </div>
                          </div>
                        ) : null}
                      </div>
                      <SheetFooter className="mt-auto pt-4 border-t border-amber-100">
                        <div className="flex flex-col space-y-2 text-sm text-muted-foreground">
                          <span className="flex items-center gap-2">
                            <Phone className="h-4 w-4" /> {t("contactPhone")}
                          </span>
                          <span className="flex items-center gap-2">
                            <Mail className="h-4 w-4" /> {t("contactEmail")}
                          </span>
                        </div>
                      </SheetFooter>
                    </SheetContent>
                  </Sheet>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  )
}
